/* eslint-disable react/prop-types */
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// api
import ApiKey from '../../Components/api/ApiKey';
import BaseApi from '../../Components/api/BaseApi';
// components
// helpers
import { logOutIfTokenExpired, resultInfo } from '../../Components/helpers';
// mui
import { useRootStore } from 'core/hooks/use-root-store';
import { useApplyJobCtx } from './apply-job-provider';

import { Dialog, Flex, Heading, Text, Box, IconButton, Checkbox } from '@radix-ui/themes';
import ApplyJobInfoForm from './step_1-user-info/ApplyJobInfoForm';
import { ResumeChooser, ApplyJobCreateResumeForm } from './step_3-resume-selection';
import { ConfirmCode } from './step_2-confirm-code';
import { ApplyWithoutResume } from './step_4-apply_by';
import { observer } from 'mobx-react-lite';
import { JobApplyStatus } from './job-apply-status';

import './apply-job.scss';

export const ApplyJobModalView = ({
   open,
   jobId,
   onClose,
   stepFrom,
   jobTitle,
   onlyResume,
   onlyQuestionnaire,
   refetch,
}) => {
   const rootStore = useRootStore();
   const applyJobStore = useApplyJobCtx();
   const user = rootStore.userStore.user;

   const navigate = useNavigate();
   const { t } = useTranslation('global');
   const tokenKey = Cookies.get('tokenClient');
   const currentLanguage = Cookies.get('selectedLanguage') || 'ukr';

   const [faculties, setFaculties] = useState([]);
   const [directories, setDirectories] = useState({});
   const [profileBody, setProfileBody] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [step, setStep] = useState(stepFrom || 'checkUser');
   const [subscribeAlert, setSubscribeAlert] = useState(true);
   const [emailConfirmCode, setEmailConfirmCode] = useState('');
   const [emailConfirmCodeError, setEmailConfirmCodeError] = useState('');
   const [jobApplyResult, setJobApplyResult] = useState({ result: null, message: '' });

   const [checkUserFormData, setCheckUserFormData] = useState({
      contact: '',
      last_name: '',
      first_name: '',
      email_address: '',
      birthday: '',
      city_id: '',
   });

   React.useEffect(() => {
      if (open) {
         setTimeout(() => (document.body.style.pointerEvents = ''), 0);
      }
   }, [open]);

   const getUserProfileData = async () => {
      try {
         const response = await axios.post(BaseApi + '/candidates/editProfile', null, {
            headers: {
               key: ApiKey,
               token: tokenKey,
               'Content-Type': 'application/json',
            },
         });

         if (response.data.status === 200) {
            const data = response.data.response;
            const userDetail = data.userdetail;
            setDirectories(data);
            setProfileBody(userDetail);
            setCheckUserFormData({
               last_name: userDetail.last_name,
               first_name: userDetail.first_name,
               contact: userDetail.contact_number,
               email_address: userDetail.email_address,
            });
         } else if (response.data.status === 400) {
            navigate('/');

            Cookies.remove('fname');
            Cookies.remove('user_id');
            Cookies.remove('user_type');
            Cookies.remove('tokenClient');

            resultInfo('warning', response.data.message, t('jobseekerEditProfile.close'));
         } else {
            resultInfo('error', response.data.message, t('jobseekerEditProfile.close'));
         }
      } catch (error) {
         if (
            error.message === 'Network Error' ||
            (error.response && error.response.data.message === 'Token has expired')
         ) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         }

         console.error(error);
      }
   };

   const handleQuestionnaireSubmit = async (e) => {
      e.preventDefault();

      try {
         setIsSubmitting(true);

         const formData = new FormData();

         jobId && formData.append('idJob', jobId);
         subscribeAlert && formData.append('subscribeAlert', subscribeAlert);
         checkUserFormData.contact && formData.append('contact', checkUserFormData.contact);
         checkUserFormData.last_name && formData.append('last_name', checkUserFormData.last_name);
         checkUserFormData.first_name &&
            formData.append('first_name', checkUserFormData.first_name);
         checkUserFormData.email_address &&
            formData.append('email_address', checkUserFormData.email_address);

         const response = await axios.post(BaseApi + '/jobApply/sentJobApply', formData, {
            headers: {
               key: ApiKey,
               token: tokenKey,
            },
         });

         if (response.data.status === 200) {
            setStep('jobApplyResult');
            setJobApplyResult((prev) => ({
               ...prev,
               result: 'success',
               message: t('jobDescription.jobAppliedSuccess'),
            }));
         } else if (response.data.status === 500) {
            setStep('jobApplyResult');
            setJobApplyResult((prev) => ({
               ...prev,
               result: 'warning',
               message: t('jobDescription.jobAlreadyApplied'),
            }));
         } else {
            setStep('jobApplyResult');
            setJobApplyResult((prev) => ({
               ...prev,
               result: 'error',
               message: t('jobDescription.applyError'),
            }));
         }

         setIsSubmitting(false);
      } catch (error) {
         setIsSubmitting(false);

         const Toast = Swal.mixin({
            toast: true,
            timer: 5000,
            position: 'top-end',
            timerProgressBar: true,
            showConfirmButton: false,
            didOpen: (toast) => {
               toast.addEventListener('mouseenter', Swal.stopTimer);
               toast.addEventListener('mouseleave', Swal.resumeTimer);
            },
         });

         Toast.fire({
            icon: 'error',
            title: t('jobDescription.applyError'),
         });
      }
   };

   useEffect(() => {
      if (step === 'jobApplySelect') {
         getUserProfileData();
      }
   }, [step]);

   const renderSubscribeAlert = () => {
      if (!profileBody.email_address) {
         return null;
      }
      if (profileBody.email_address?.includes('@email.grc.ua')) {
         return null;
      }
      return (
         <Flex gap="2" justify="center" align="items">
            <Checkbox
               color="blue"
               highContrast
               id="subscribeAlert"
               name="subscribeAlert"
               size="3"
               checked={subscribeAlert}
               onCheckedChange={(ch) => {
                  setSubscribeAlert(ch);
               }}
            />
            <label className="subscribeAlertLabel" htmlFor="subscribeAlert">
               {t('jobDescription.subscribeAlert')}
            </label>
         </Flex>
      );
   };

   const subscribeAlertView = renderSubscribeAlert();

   return (
      <Dialog.Root
         open={open}
         onOpenChange={onClose}
         aria-labelledby="apply-job-modal"
         aria-describedby="apply-job-modal-description"
      >
         <Dialog.Content
            size="3"
            className='apply-job-dialog'
            minWidth={{ initial: '100%', md: step === 'createResume' ? '964px' : '450px' }}
            maxWidth={{
               initial: '100%',
               md: step === 'createResume' ? '964px' : '600px',
            }}
            onInteractOutside={(e) => {
               e.preventDefault();
            }}
         >
            <Box onClick={onClose} className="">
               <div style={{ width: '100%' }} onClick={(e) => e.stopPropagation()} className="">
                  <div className="modal-content container">
                     <div className="modal-body px-0 jobApplyModal">
                        {step === 'checkUser' && (
                           <div>
                              <div className="mx-auto">
                                 <ApplyJobInfoForm
                                    onCheckUserSuccess={() => {
                                       setStep('checkEmailCode');
                                    }}
                                 />
                              </div>
                              <Link
                                 className="my-4 fs-6 d-block text-center"
                                 to="/user/register/employer"
                              >
                                 {t('jobDescription.employerRegister')}
                              </Link>
                           </div>
                        )}
                        {step === 'checkEmailCode' && (
                           <ConfirmCode
                              phoneConfirmed={applyJobStore.isPhoneConfirmed}
                              checkUserMessage={applyJobStore.checkUserMessage}
                              setStep={setStep}
                              emailConfirmCode={emailConfirmCode}
                           />
                        )}
                        {step === 'jobApplySelect' && (
                           <ResumeChooser
                              jobTitle={jobTitle}
                              jobId={jobId}
                              setStep={setStep}
                              onlyResume={onlyResume}
                              isSubmitting={isSubmitting}
                              user={rootStore.userStore.user}
                              setIsSubmitting={setIsSubmitting}
                              emailConfirmCode={emailConfirmCode}
                              checkUserFormData={checkUserFormData}
                              onlyQuestionnaire={onlyQuestionnaire}
                              setJobApplyResult={setJobApplyResult}
                              subscribeAlert={subscribeAlertView}
                              setEmailConfirmCodeError={setEmailConfirmCodeError}
                              onApply={() => {
                                 setStep('jobApplyResult');
                              }}
                           />
                        )}
                        {step === 'createResume' && (
                           <ApplyJobCreateResumeForm
                              step={step}
                              setStep={setStep}
                              faculties={faculties}
                              directories={directories}
                              profileBody={profileBody}
                              setFaculties={setFaculties}
                              subscribeAlert={subscribeAlertView}
                           />
                        )}
                        {step === 'questionnaire' && (
                           <ApplyWithoutResume
                              jobTitle={jobTitle}
                              jobId={jobId}
                              user={rootStore.userStore.user}
                              store={applyJobStore.withoutResumeStore}
                              onlyResume={onlyResume}
                              onlyQuestionnaire={onlyQuestionnaire}
                              subscribeAlert={subscribeAlertView}
                              onBack={() => {
                                 setStep('jobApplySelect');
                              }}
                              onApply={(isMissing) => {
                                 setStep('jobApplyResult');
                                 if (isMissing) {
                                    rootStore.userStore.chekUser();
                                 }
                              }}
                           />
                        )}

                        {step === 'jobApplyResult' && (
                           <div>
                              <JobApplyStatus status={applyJobStore.status} />
                              <div className="d-flex justify-content-evenly">
                                 <button
                                    type="submit"
                                    onClick={onClose}
                                    className="mt-0 mb-5 btn button1"
                                 >
                                    {t('jobDescription.viewOtherVacancies')}
                                 </button>
                              </div>
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </Box>
         </Dialog.Content>
      </Dialog.Root>
   );
};

export const ApplyJobModal = observer(ApplyJobModalView);

export default ApplyJobModal;
